import { useState, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { fmtCurrency, fmtPercent, fmtPercentRaw, fmtPSF, fmtSF, fmtCamType } from "../utils/format";
import type { ReimbursementDetail } from "../api/types";

/* -------------------------------------------------------------------------- */
/* C5: PSF / Total $ View Toggle                                              */
/* -------------------------------------------------------------------------- */

export type ViewMode = "total" | "psf";

export function ViewModeToggle({
  mode,
  setMode,
}: {
  mode: ViewMode;
  setMode: (m: ViewMode) => void;
}) {
  return (
    <div className="flex items-center gap-1 bg-sage-100 rounded-lg p-0.5">
      <button
        onClick={() => setMode("total")}
        className={`px-3 py-1 text-[11px] font-semibold rounded-md transition-colors ${
          mode === "total"
            ? "bg-white text-bark-700 shadow-sm"
            : "text-bark-400 hover:text-bark-600"
        }`}
      >
        Total $
      </button>
      <button
        onClick={() => setMode("psf")}
        className={`px-3 py-1 text-[11px] font-semibold rounded-md transition-colors ${
          mode === "psf"
            ? "bg-white text-bark-700 shadow-sm"
            : "text-bark-400 hover:text-bark-600"
        }`}
      >
        Per SF
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* C1: Recovery Rate Trend Chart                                              */
/* -------------------------------------------------------------------------- */

interface RecoveryChartProps {
  yearTotals: Array<{ year: number; total: number }>;
  annualCashFlows: Array<Record<string, number>> | undefined;
}

export function RecoveryRateTrendChart({
  yearTotals,
  annualCashFlows,
}: RecoveryChartProps) {
  const data = useMemo(() => {
    if (!annualCashFlows || annualCashFlows.length === 0) return [];
    return yearTotals.map((yt, i) => {
      const acf = annualCashFlows[i];
      const opex = acf?.total_operating_expenses ?? 0;
      const reimb =
        acf?.total_reimbursement_income ?? yt.total;
      const rate = opex > 0 ? reimb / opex : 0;
      return {
        year: `Yr ${yt.year}`,
        rate: Math.round(rate * 10000) / 100, // percent with 2 decimals
      };
    });
  }, [yearTotals, annualCashFlows]);

  const hasHighRecovery = data.some(d => d.rate > 100);

  if (data.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-grove-300 shadow-sm">
      <div className="px-6 py-5 border-b border-grove-200">
        <h3 className="font-display text-sm font-semibold text-bark-700">
          Recovery Rate Trend
        </h3>
        <p className="text-xs text-bark-300 mt-1">
          Reimbursement income as a percentage of total operating expenses per
          year
        </p>
        {hasHighRecovery && (
          <p className="text-xs text-red-600 mt-2">
            One or more years exceed 100% recovery — review reimbursement assumptions
          </p>
        )}
      </div>
      <div className="px-4 py-4" style={{ height: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#D4D0C8" />
            <XAxis
              dataKey="year"
              tick={{ fontSize: 11, fill: "#6B6B60" }}
              axisLine={{ stroke: "#D4D0C8" }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#6B6B60" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v: number) => fmtPercentRaw(v, 0)}
              domain={[0, "auto"]}
            />
            <Tooltip
              formatter={(value: number | undefined) => [fmtPercentRaw(value ?? 0, 1), "Recovery Rate"]}
              contentStyle={{
                fontSize: 12,
                borderColor: "#D4D0C8",
                borderRadius: 8,
              }}
            />
            <Line
              type="monotone"
              dataKey="rate"
              stroke="#2D5016"
              strokeWidth={3}
              dot={{ fill: "#2D5016", stroke: "#2D5016", r: 4 }}
              activeDot={{ r: 6, fill: "#C8860A" }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* C2: Tenant Recovery Comparison Table                                       */
/* -------------------------------------------------------------------------- */

interface ComparisonTableProps {
  details: ReimbursementDetail[];
  years: number[];
  propTenants: Array<Record<string, unknown>>;
  viewMode: ViewMode;
}

export function TenantRecoveryComparisonTable({
  details,
  years,
  propTenants,
  viewMode,
}: ComparisonTableProps) {
  const [sortCol, setSortCol] = useState<string>("name");
  const [sortAsc, setSortAsc] = useState(true);

  const tenantRows = useMemo(() => {
    const tenantMap = new Map<
      string,
      {
        name: string;
        leased_sf: number;
        camType: string;
        proRata: number;
        yearAmounts: Record<number, number>;
      }
    >();
    for (const d of details) {
      if (!tenantMap.has(d.tenant_id)) {
        const info = propTenants.find((t) => t.tenant_id === d.tenant_id);
        const camType =
          (
            (info?.cam_structure as Record<string, unknown>)?.cam_type as string
          ) ?? "--";
        tenantMap.set(d.tenant_id, {
          name: d.tenant_name,
          leased_sf: (info?.leased_sf as number) || 0,
          camType,
          proRata: d.pro_rata_share,
          yearAmounts: {},
        });
      }
      tenantMap.get(d.tenant_id)!.yearAmounts[d.year] = d.total_reimbursement;
    }
    let rows = Array.from(tenantMap.values());

    rows.sort((a, b) => {
      let cmp = 0;
      if (sortCol === "name") cmp = a.name.localeCompare(b.name);
      else if (sortCol === "sf") cmp = a.leased_sf - b.leased_sf;
      else if (sortCol === "proRata") cmp = a.proRata - b.proRata;
      else if (sortCol === "cam") cmp = a.camType.localeCompare(b.camType);
      else {
        const yr = parseInt(sortCol);
        cmp = (a.yearAmounts[yr] ?? 0) - (b.yearAmounts[yr] ?? 0);
      }
      return sortAsc ? cmp : -cmp;
    });
    return rows;
  }, [details, propTenants, sortCol, sortAsc]);

  function toggleSort(col: string) {
    if (sortCol === col) setSortAsc(!sortAsc);
    else {
      setSortCol(col);
      setSortAsc(true);
    }
  }

  const arrow = (col: string) =>
    sortCol === col ? (sortAsc ? " \u25B4" : " \u25BE") : "";

  function fmtVal(amount: number, sf: number): string {
    if (viewMode === "psf" && sf > 0) {
      return fmtPSF(amount / sf);
    }
    return fmtCurrency(amount);
  }

  return (
    <div className="bg-white rounded-xl border border-grove-300 shadow-sm">
      <div className="px-6 py-5 border-b border-grove-200">
        <h3 className="font-display text-sm font-semibold text-bark-700">
          Tenant Recovery Comparison
        </h3>
        <p className="text-xs text-bark-300 mt-1">
          Side-by-side recovery structure for all tenants
          {viewMode === "psf" && " (per square foot)"}
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full data-table text-[11px]">
          <thead>
            <tr>
              <th
                className="min-w-[150px] cursor-pointer select-none"
                onClick={() => toggleSort("name")}
              >
                Tenant{arrow("name")}
              </th>
              <th
                className="num cursor-pointer select-none"
                onClick={() => toggleSort("sf")}
              >
                SF{arrow("sf")}
              </th>
              <th
                className="cursor-pointer select-none"
                onClick={() => toggleSort("cam")}
              >
                CAM Type{arrow("cam")}
              </th>
              <th
                className="num cursor-pointer select-none"
                onClick={() => toggleSort("proRata")}
              >
                Pro Rata{arrow("proRata")}
              </th>
              {years.map((yr) => (
                <th
                  key={yr}
                  className="num cursor-pointer select-none"
                  onClick={() => toggleSort(String(yr))}
                >
                  Yr {yr}
                  {arrow(String(yr))}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tenantRows.map((row) => (
              <tr key={row.name}>
                <td className="font-medium text-bark-700 whitespace-nowrap">
                  {row.name}
                </td>
                <td className="num tabular-nums">
                  {row.leased_sf > 0 ? fmtSF(row.leased_sf) : "--"}
                </td>
                <td>
                  <span className="text-[10px] px-1.5 py-0.5 rounded border border-grove-200 bg-sage-100 text-bark-600 font-medium whitespace-nowrap">
                    {fmtCamType(row.camType)}
                  </span>
                </td>
                <td className="num tabular-nums">
                  {fmtPercent(row.proRata)}
                </td>
                {years.map((yr) => (
                  <td key={yr} className="num tabular-nums">
                    {fmtVal(row.yearAmounts[yr] ?? 0, row.leased_sf)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
